import React, { useState } from 'react';
import { 
  Action, 
  ActionNode, 
  ACTION_PRIORITY_CONFIG, 
  ACTION_TYPE_CONFIG,
  ActionPriority,
  ActionType as ActionTypeEnum,
  TeamMember
} from '../types';
import InlineEditableText from './InlineEditableText';
import CompletionDot from './CompletionDot';
import { createActionDragData, setActionDragData } from './actionDragData';
import AssignmentDropdown from '../team/AssignmentDropdown';

// Support both Firebase types and legacy Node types for backward compatibility
type ActionDataType = Action | ActionNode;

interface ActionRowProps {
  action: ActionDataType;
  onToggleComplete: (id: string) => void;
  onUpdateTitle: (id: string, title: string) => void;
  onPlanInAgenda?: (action: ActionDataType) => void;
  onDelete?: (id: string) => void;
  showPlanButton?: boolean;
  isFocusAction?: boolean;  // NIEUW - subtiele highlight voor focus actie
  actionNumber?: number;  // NIEUW - globale nummering voor overzicht
  showPriorityBadge?: boolean;  // Toon prioriteit badge
  showTypeBadge?: boolean;      // Toon type badge
  // Team collaboration props
  isTeamGoal?: boolean;
  teamMembers?: TeamMember[];
  onAssign?: (actionId: string, userId: string | null) => void;
}

/**
 * ActionRow
 * 
 * A simple row for displaying an action.
 * - Title (inline editable)
 * - Completion dot (clickable)
 * - "Plan in agenda" button (hover-only)
 * - No checkbox appearance
 */
const ActionRow: React.FC<ActionRowProps> = ({
  action,
  onToggleComplete,
  onUpdateTitle,
  onPlanInAgenda,
  onDelete,
  showPlanButton = true,
  isFocusAction = false,  // NIEUW - opt-in subtiele highlight
  actionNumber,  // NIEUW - opt-in nummering
  showPriorityBadge = false,  // Default: uit (backwards compatible)
  showTypeBadge = false,      // Default: uit (backwards compatible)
  isTeamGoal = false,
  teamMembers = [],
  onAssign,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Check if this is a Firebase Action (has goalId) for drag & drop
  const isFirebaseAction = 'goalId' in action;
  
  // Get priority and type from action (only available on Firebase Action type)
  const priority: ActionPriority | undefined = 'priority' in action ? action.priority : undefined;
  const actionType: ActionTypeEnum | undefined = 'actionType' in action ? action.actionType : undefined;
  
  // Get config for badges
  const priorityConfig = priority ? ACTION_PRIORITY_CONFIG[priority] : null;
  const typeConfig = actionType ? ACTION_TYPE_CONFIG[actionType] : null;

  // Drag handlers
  const handleDragStart = (e: React.DragEvent) => {
    if (!isFirebaseAction) return;
    
    const dragData = createActionDragData(action as Action);
    setActionDragData(e.dataTransfer, dragData);
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <div
      draggable={isFirebaseAction}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={`
        group
        flex items-center justify-between
        py-2 px-2 -mx-2
        rounded-md
        transition-colors duration-150
        ${isHovered ? 'bg-gray-50' : ''}
        ${isFocusAction && !action.completed ? 'bg-blue-50/30' : ''}
        ${isDragging ? 'opacity-50' : ''}
        ${isFirebaseAction ? 'cursor-move' : ''}
      `.trim()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Left side: number + completion + badges + title */}
      <div className="flex items-center space-x-2 flex-1 min-w-0">
        {actionNumber !== undefined && (
          <span className={`
            text-xs font-medium w-6 text-right flex-shrink-0
            ${action.completed ? 'text-gray-400' : 'text-gray-500'}
          `}>
            {actionNumber}.
          </span>
        )}
        
        {/* Priority indicator dot (always visible if set) */}
        {priorityConfig && !action.completed && (
          <span 
            className={`w-2 h-2 rounded-full flex-shrink-0 ${priorityConfig.dotColor}`}
            title={`Prioriteit: ${priorityConfig.label}`}
          />
        )}
        
        <CompletionDot
          completed={!!action.completed}
          onClick={() => onToggleComplete(action.id)}
          interactive
          size="md"
        />
        
        {/* Type badge (compact, always visible if enabled) */}
        {showTypeBadge && typeConfig && !action.completed && (
          <span 
            className={`
              text-[9px] px-1.5 py-0.5 rounded flex-shrink-0
              ${typeConfig.bgColor} ${typeConfig.textColor} border ${typeConfig.borderColor}
            `}
            title={typeConfig.label}
          >
            {typeConfig.emoji}
          </span>
        )}
        
        <InlineEditableText
          value={action.title}
          onSave={(newTitle) => onUpdateTitle(action.id, newTitle)}
          placeholder="Actie naam..."
          className={`
            text-sm flex-1 truncate
            ${action.completed ? 'text-gray-400 line-through' : 'text-gray-700'}
          `}
        />
        
        {/* Assignment dropdown for all goals with team members */}
        {teamMembers && teamMembers.length > 0 && onAssign && 'assignedTo' in action && (
          <AssignmentDropdown
            currentAssignee={action.assignedTo}
            teamMembers={teamMembers}
            onAssign={(userId) => onAssign(action.id, userId)}
            disabled={action.completed}
          />
        )}
      </div>

      {/* Right side: badges + time estimate + actions (hover-only) */}
      <div className={`
        flex items-center space-x-2
        transition-opacity duration-150
        ${isHovered ? 'opacity-100' : 'opacity-0'}
      `}>
        {/* Priority badge (on hover, if enabled) */}
        {showPriorityBadge && priorityConfig && !action.completed && (
          <span 
            className={`
              text-[9px] px-1.5 py-0.5 rounded
              ${priorityConfig.bgColor} ${priorityConfig.textColor} border ${priorityConfig.borderColor}
            `}
          >
            {priorityConfig.emoji} {priorityConfig.label}
          </span>
        )}
        
        {/* Time estimate badge (if set) - only for ActionNode type */}
        {'estimatedMinutes' in action && action.estimatedMinutes && action.estimatedMinutes > 0 && (
          <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
            {action.estimatedMinutes}m
          </span>
        )}

        {/* Plan in agenda button */}
        {showPlanButton && onPlanInAgenda && !action.completed && (
          <button
            onClick={() => onPlanInAgenda(action)}
            className="
              text-[11px] text-gray-500 
              hover:text-orange-600 hover:bg-orange-50
              px-2 py-1 rounded
              transition-colors duration-150
            "
          >
            Plan in agenda
          </button>
        )}

        {/* Delete button */}
        {onDelete && (
          <button
            onClick={() => onDelete(action.id)}
            className="
              text-gray-400 hover:text-red-500
              p-1 rounded
              transition-colors duration-150
            "
            title="Verwijder actie"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default ActionRow;
